import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, RefreshCw, Mail, MessageCircle, Send, Loader2, Inbox, Bot, FileText } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { EnquiryRow } from "../components/admin/EnquiryRow";
import { AdminTabs } from "../components/admin/AdminTabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
const STATUS_TABS = ["all", "new", "contacted", "quoted", "closed"];
const authHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem("ib_access_token")}` });

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState("all");
  const [source, setSource] = useState("all");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [e, s] = await Promise.all([
        axios.get(`${API}/enquiries`, { params: { status, source, q }, headers: authHeaders() }),
        axios.get(`${API}/stats`, { headers: authHeaders() }),
      ]);
      setItems(e.data.enquiries);
      setStats(s.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load enquiries");
    } finally {
      setBusy(false);
    }
  }, [status, source, q]);

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") { navigate("/login"); return; }
    load();
    axios.get(`${API}/alerts/status`, { headers: authHeaders() }).then((r) => setAlerts(r.data)).catch(() => {});
  }, [user, loading, navigate, load]);

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.patch(`${API}/enquiries/${id}`, { status: newStatus }, { headers: authHeaders() });
      toast.success(`Marked as ${newStatus}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await axios.post(`${API}/alerts/test`, {}, { headers: authHeaders() });
      toast[data.email_sent || data.whatsapp_sent ? "success" : "warning"](
        `Email: ${data.email_sent ? "sent" : "not configured"} · WhatsApp: ${data.whatsapp_sent ? "sent" : "not configured"}`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  if (loading || !user || user.role !== "admin") return null;

  return (
    <div className="min-h-screen pt-28 pb-20 px-6" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        <AdminTabs active="enquiries" />
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <span className="font-jetbrains text-xs text-ib-lime tracking-widest uppercase">Admin</span>
            <h1 className="font-outfit font-extrabold text-3xl sm:text-4xl tracking-tight mt-2 text-ib-text">Enquiry Dashboard</h1>
            <p className="font-manrope text-sm text-ib-text-secondary mt-2">Leads collected by Payal and the quote form. Mark them as you work through them.</p>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="alert-status">
            <AlertBadge icon={Mail} label="Email" cfg={alerts?.email} />
            <AlertBadge icon={MessageCircle} label="WhatsApp" cfg={alerts?.whatsapp} />
            <Button data-testid="send-test-alert-btn" onClick={sendTest} disabled={testing} variant="outline" size="sm" className="border-ib-steel/40 text-ib-text-secondary hover:text-ib-lime font-outfit text-xs gap-1.5">
              {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send test alert
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
          <StatCard icon={Inbox} label="Total leads" value={stats?.total} />
          <StatCard icon={Bot} label="Via Payal" value={stats?.chatbot} />
          <StatCard icon={FileText} label="Via quote form" value={stats?.quote_form} />
          <StatCard icon={Send} label="Awaiting contact" value={stats?.by_status?.new} accent />
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-6">
          <div className="flex gap-1 p-1 rounded-full border border-ib-steel/30 bg-ib-surface/50 overflow-x-auto">
            {STATUS_TABS.map((s) => (
              <button key={s} data-testid={`status-tab-${s}`} onClick={() => setStatus(s)}
                className={`px-4 py-1.5 rounded-full font-outfit text-xs capitalize whitespace-nowrap transition-colors ${status === s ? "bg-ib-blue text-white" : "text-ib-text-secondary hover:text-ib-text"}`}>
                {s}{s !== "all" && stats ? ` (${stats.by_status[s]})` : ""}
              </button>
            ))}
          </div>
          <select data-testid="source-filter" value={source} onChange={(e) => setSource(e.target.value)}
            className="h-9 rounded-md border border-ib-steel/30 bg-ib-bg/50 px-3 font-manrope text-xs text-ib-text">
            <option value="all">All sources</option>
            <option value="chatbot_payal">Payal chatbot</option>
            <option value="quote_form">Quote form</option>
          </select>
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ib-text-muted" />
            <Input data-testid="enquiry-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, material, PIN..."
              className="pl-9 bg-ib-bg/50 border-ib-steel/30 text-ib-text placeholder:text-ib-text-muted font-manrope text-sm" />
          </div>
          <Button data-testid="refresh-btn" onClick={load} variant="ghost" size="sm" className="text-ib-text-secondary hover:text-ib-blue">
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
          </Button>
        </div>

        <div className="space-y-3" data-testid="enquiry-list">
          {items.length === 0 && !busy && (
            <div data-testid="enquiry-empty" className="glass rounded-xl p-12 text-center font-manrope text-sm text-ib-text-muted">No enquiries match these filters.</div>
          )}
          {items.map((item) => <EnquiryRow key={item.id} item={item} onStatus={updateStatus} />)}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="glass rounded-xl p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="font-manrope text-xs text-ib-text-muted">{label}</span>
        <Icon className={`w-4 h-4 ${accent ? "text-ib-lime" : "text-ib-blue"}`} />
      </div>
      <div className={`font-jetbrains text-3xl font-bold mt-2 ${accent ? "text-ib-lime" : "text-ib-text"}`}>{value ?? "—"}</div>
    </div>
  );
}

function AlertBadge({ icon: Icon, label, cfg }) {
  const ok = cfg?.configured;
  return (
    <span data-testid={`alert-badge-${label.toLowerCase()}`} title={cfg?.recipients?.join(", ")}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border font-jetbrains text-[10px] ${ok ? "border-ib-lime/40 text-ib-lime bg-ib-lime/5" : "border-ib-warning/40 text-ib-warning bg-ib-warning/5"}`}>
      <Icon className="w-3 h-3" /> {label}: {cfg ? (ok ? "active" : "key missing") : "…"}
    </span>
  );
}
